import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid2";
import { AxiosError } from "axios";
import {
  BarElement,
  Chart as ChartJS,
  Legend,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import { useAtom } from "jotai/index";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { useStatDiversityPageQuery } from "@/api/hooks/stat-analytics-hooks";
import { useIsOwnerOrSuperAdmin } from "@/auth/hooks";
import { useTableSettings } from "@/hooks/use-table-settings";
import { DataOutdatedAlert } from "@/module/service-configs-module/statistics-visualization-module/components/data-outdated-alert";
import { colorPool } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/util";
import { useCreateSession } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/hooks/use-create-session";
import { useGetSessionStats } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/hooks/use-get-session-stats";
import { useGetSessions } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/hooks/use-get-sessions";
import { useGetStatsList } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/hooks/use-get-stats-list";
import { SciChart } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/sci-chart";
import { round } from "@/module/service-configs-module/statistics-visualization-module/stat-diversity/utils";
import { isSubmitLoading } from "@/module/service-configs-module/statistics-visualization-module/store";
import {
  MoneyFlowRequestDto,
  PlayerStatsDto,
  QuerySessionStatsRequestDto,
  StatFilterDtoModeEnum,
  StatItemDto,
  StatsSessionDto,
} from "@/shared/api";
import { useApi } from "@/shared/api/use-api";
import { meStore } from "@/store/store";

ChartJS.register(LinearScale, PointElement, BarElement, Title, Tooltip, Legend);

type StatDiversityPageProps = {
  filters: MoneyFlowRequestDto | null;
  isDataOutdated: boolean;
};

const MINUTES_10 = 60 * 10;
const STATS_COLUMN_FILTER = ["equals", "notEquals", "between", "greaterThan", "lessThan"];
const FIRST_COLS = ["Hands", "VPIP", "PFR", "3bet"];

export const StatDiversityPage = ({ filters, isDataOutdated }: StatDiversityPageProps) => {
  const { t } = useTranslation();
  const { statsControllerApi } = useApi();
  const isInitialized = useRef(false);
  const [isDurable, setIsDurable] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [isShowOtherUserSessions, setIsShowOtherUserSessions] = useState(false);
  const sessionId = Object.keys(rowSelection)[0];
  const isOwner = useIsOwnerOrSuperAdmin();
  const [me] = useAtom(meStore);
  const [, setIsSubmitLoading] = useAtom(isSubmitLoading);
  const { data: statDiversityData, isLoading: isDiversityDataLoading } =
    useStatDiversityPageQuery(filters);
  const { data: statsListResponse, isLoading: isGetStatsListLoading } = useGetStatsList();
  const {
    data: sessionsResponse,
    isLoading: isSessionsLoading,
    refetch: refetchSession,
  } = useGetSessions(isShowOtherUserSessions ? undefined : me?.id);
  const checkSessionInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const [sessionExpiringSeconds, setSessionExpiringSeconds] = useState<number | null>(null);
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);

  const userId = useMemo(() => {
    return sessionsResponse?.data.sessions.find((item) => item.session_id === sessionId)
      ?.user_id;
  }, [sessionId, sessionsResponse]);

  const { mutateAsync: createSession } = useCreateSession();

  useEffect(() => {
    const create = async () => {
      if (!filters) {
        return;
      }

      try {
        setIsSubmitLoading(true);
        await createSession({
          query: filters,
          is_durable: isDurable,
        });
        await refetchSession();
      } catch (e) {
        const message =
          (e as unknown as AxiosError<{ message: string }>)?.response?.data?.message || e;

        enqueueSnackbar(message as string, {
          variant: "error",
          autoHideDuration: 2000,
        });
      } finally {
        setIsSubmitLoading(false);
      }
    };
    create();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsSubmitLoading, filters]);

  useEffect(() => {
    if (statDiversityData) {
      isInitialized.current = !!statDiversityData;
    }
  }, [statDiversityData]);

  useEffect(() => {
    if (sessionId) {
      checkSessionInterval.current = setInterval(() => {
        const currentSession = sessionsResponse?.data.sessions.find(
          (item) => item.session_id === sessionId
        );
        if (currentSession) {
          setSessionExpiringSeconds(
            Math.abs(dayjs(currentSession.eviction_at).diff(new Date(), "seconds"))
          );
        }
      }, 1000);
    }

    return () => {
      if (checkSessionInterval.current) {
        clearInterval(checkSessionInterval.current);
      }
    };
  }, [sessionId, sessionsResponse?.data.sessions]);

  const firstStats = useMemo(() => {
    const filtered =
      statsListResponse?.data.items.filter((item) => FIRST_COLS.includes(item.stat_name)) ||
      [];

    return FIRST_COLS.map((item) => {
      return filtered.find((stat) => stat.stat_name === item) as StatItemDto;
    }).filter((item) => !!item);
  }, [statsListResponse?.data.items]);

  const columns = useMemo<MRT_ColumnDef<PlayerStatsDto>[]>(() => {
    return [
      {
        header: t("pid"),
        accessorKey: "player_id",
        filterFn: "equals",
        enableColumnFilterModes: false,
      },
      {
        header: t("aiProfile"),
        accessorKey: "ai_profile",
        filterFn: "equals",
        enableColumnFilterModes: false,
      },
      {
        header: t("totalBBProfit"),
        accessorKey: "total_bb_profit",
        filterFn: "equals",
        enableColumnFilterModes: false,
        Cell: ({ cell }) => {
          if (cell.row.original.ai_profile === "") {
            const value = cell.getValue<number>();
            if (value) {
              return round(cell.getValue<number>());
            }

            return value || "0";
          }
          return "";
        },
      },
      ...(firstStats?.map((item) => {
        let header = item.stat_name;
        if (item.stat_name === "Hands") {
          header = t("hands");
        }
        return {
          id: item.stat_id,
          header,
          accessorFn: ({ stats }) => {
            return stats?.[item.stat_id || item.stat_name];
          },
          columnFilterModeOptions: STATS_COLUMN_FILTER,
          Cell: ({ cell }) => {
            const value = cell.getValue<number>();
            if (value) {
              return round(cell.getValue<number>());
            }

            return value || "0";
          },
        } as MRT_ColumnDef<PlayerStatsDto>;
      }) || []),
      ...(statsListResponse?.data.items
        .filter((item) => !FIRST_COLS.includes(item.stat_name))
        .sort((a, b) => a.stat_name.localeCompare(b.stat_name))
        .map((item) => {
          return {
            id: item.stat_id,
            header: item.stat_name,
            accessorFn: ({ stats }) => {
              return stats?.[item.stat_id];
            },
            Cell: ({ cell }) => {
              const value = cell.getValue<number>();
              if (value) {
                return round(cell.getValue<number>());
              }

              return value || "0";
            },
            muiTableHeadCellProps: {
              sx: {
                minWidth: "175px",
              },
            },
            columnFilterModeOptions: STATS_COLUMN_FILTER,
          } as MRT_ColumnDef<PlayerStatsDto>;
        }) || []),
    ];
  }, [t, statsListResponse, firstStats]);

  const { defaultProps, state } = useTableSettings<PlayerStatsDto>("statsDiversity");

  const columnFilterFns = useMemo(() => {
    return {
      ...(statsListResponse?.data.items.reduce<Record<string, string>>((acc, item) => {
        acc[item.stat_id] = "between";
        return acc;
      }, {}) || {}),
      ...state.columnFilterFns,
    };
  }, [statsListResponse?.data.items, state.columnFilterFns]);

  const statsFilters: QuerySessionStatsRequestDto = useMemo(() => {
    const filterFns = Object.keys(columnFilterFns);
    const playerIdFilter = state.columnFilters.find((item) => item.id === "player_id");
    const aiProfileFilter = state.columnFilters.find((item) => item.id === "ai_profile");

    return {
      pid_filter: {
        pids: playerIdFilter ? [playerIdFilter.value as string] : [],
      },
      ai_profile_filter: {
        ai_profiles: aiProfileFilter ? [aiProfileFilter.value as string] : [],
      },
      stat_filters: state.columnFilters
        .filter((item) => item.id !== "player_id" && item.id !== "ai_profile")
        .map((item) => {
          return {
            stat_id: item.id,
            mode: columnFilterFns[
              item.id as keyof typeof filterFns
            ].toUpperCase() as StatFilterDtoModeEnum,
            stat_value: Array.isArray(item.value)
              ? item.value.join(",")
              : (item.value as string),
          };
        }),
    };
  }, [state.columnFilters, columnFilterFns]);

  const { data: statsResponse, isLoading: isSessionStatsLoading } = useGetSessionStats({
    sessionId,
    userId,
    pageNumber: state.pagination.pageIndex,
    pageSize: state.pagination.pageSize,
    filters: statsFilters,
  });

  const table = useMaterialReactTable({
    data: statsResponse?.data.items || [],
    columns: columns,
    getRowId: (originalRow) => originalRow.player_id,
    ...defaultProps,
    initialState: {
      ...defaultProps.initialState,
      sorting: [{ id: "name", desc: false }],
    },
    enableGrouping: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    groupedColumnMode: "reorder",
    manualPagination: true,
    manualFiltering: true,
    rowCount: statsResponse?.data.pagination.items_total,
    state: {
      ...state,
      columnFilterFns,
      sorting: state.sorting.length > 0 ? state.sorting : [{ id: "player_id", desc: false }],
      isLoading: isSessionStatsLoading,
      pagination: {
        pageSize: statsResponse?.data.pagination.page_size || state.pagination.pageSize,
        pageIndex: statsResponse?.data.pagination.page_number || state.pagination.pageIndex,
      },
    },
    muiToolbarAlertBannerProps: {
      sx: {
        "& .MuiAlert-message": {
          minWidth: "500px !important",
        },
      },
    },
    renderTopToolbarCustomActions: () => {
      return (
        <Button
          variant="contained"
          onClick={handleExportSessionStats}
          loading={isExportLoading}
        >
          {t("export")}
        </Button>
      );
    },
    muiTableBodyRowProps: ({ row }) => {
      if (row.original.ai_profile === "") {
        if (row.original.total_bb_profit >= 0) {
          return {
            sx: {
              backgroundColor: alpha(colorPool[1], 0.25),
            },
          };
        } else {
          return {
            sx: {
              backgroundColor: alpha(colorPool[0], 0.25),
            },
          };
        }
      }

      return {};
    },
  });

  const handleExportSessionStats = async () => {
    if (isExportLoading || !sessionId || !userId) {
      return;
    }

    try {
      setIsExportLoading(true);
      const response = await statsControllerApi.exportSessionStatsCsv(userId, sessionId, {
        responseType: "blob",
      });
      const contentDisposition = response?.headers["content-disposition"];
      if (contentDisposition) {
        const fileName = contentDisposition.split("filename=")[1].split(";")[0];
        const url = URL.createObjectURL(response.data as Blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (e) {
      const message =
        (e as unknown as AxiosError<{ message: string }>)?.response?.data?.message || e;

      enqueueSnackbar(message as string, {
        variant: "error",
        autoHideDuration: 2000,
      });
    } finally {
      setIsExportLoading(false);
    }
  };

  const { defaultProps: defaultSessionTableProps, state: defaultSessionState } =
    useTableSettings<StatsSessionDto>("statsSession", {
      pageSize: 10,
    });

  const sessionColumns = useMemo<MRT_ColumnDef<StatsSessionDto>[]>(() => {
    return [
      {
        header: t("sessionId"),
        accessorKey: "session_id",
        id: "session_id",
      },
      {
        header: t("status"),
        accessorKey: "status",
        id: "status",
        Cell: ({ row: { original } }) => {
          switch (original.status) {
            case "DATA_PROCESSING_ERROR":
              return t("dataProcessingError");
            case "EVICTED":
              return t("evicted");
            case "READY":
              return t("ready");
            case "CREATED":
              return (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <span>{t("created")}</span>
                  <CircularProgress size={16} />
                </Box>
              );
            default:
              return original.status;
          }
        },
      },
      {
        header: t("userId"),
        accessorKey: "user_id",
        id: "user_id",
      },
      {
        header: t("createdAt"),
        accessorKey: "created_at",
        id: "created_at",
        Cell: ({ row: { original } }) =>
          original.created_at ? dayjs(original.created_at).format("DD.MM.YYYY HH:mm") : "-",
      },
      {
        header: t("evictionAt"),
        accessorKey: "eviction_at",
        id: "eviction_at",
        Cell: ({ row: { original } }) =>
          original.eviction_at ? dayjs(original.eviction_at).format("DD.MM.YYYY HH:mm") : "-",
      },
    ];
  }, [t]);

  const sessionsTable = useMaterialReactTable({
    data: sessionsResponse?.data?.sessions || [],
    columns: sessionColumns,
    getRowId: (originalRow) => originalRow.session_id,
    ...defaultSessionTableProps,
    initialState: {
      ...defaultSessionTableProps.initialState,
    },
    enableColumnOrdering: true,
    enableColumnDragging: true,
    enableColumnFilterModes: true,
    enableRowSelection: ({ original }) => {
      return original.status === "READY";
    },
    enableMultiRowSelection: false,
    onRowSelectionChange: (param) => {
      setRowSelection(param);
    },
    state: {
      ...defaultSessionState,
      rowSelection,
      isLoading: isSessionsLoading,
    },
    muiToolbarAlertBannerProps: {
      sx: {
        "& .MuiAlert-message": {
          minWidth: "500px !important",
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        width: "100%",
      },
    },
    renderTopToolbarCustomActions: () => {
      return isOwner ? (
        <FormControlLabel
          control={
            <Checkbox
              checked={isShowOtherUserSessions}
              onChange={(e) => setIsShowOtherUserSessions(e.target.checked)}
            />
          }
          label={t("showOtherUserSessions")}
        />
      ) : null;
    },
  });

  const buttonsContainer = document.querySelector(
    "#statisticVisualizationFiltersButtonsContainer"
  );

  return (
    <>
      <Grid container paddingY={2} rowSpacing={2} columnSpacing={2}>
        {isGetStatsListLoading ? (
          <CircularProgress sx={{ margin: "96px auto 32px" }} />
        ) : (
          <>
            <Stack sx={{ display: "flex", gap: 2, alignItems: "flex-start", width: "100%" }}>
              {userId &&
                sessionExpiringSeconds !== null &&
                sessionExpiringSeconds < MINUTES_10 && (
                  <Alert
                    severity="warning"
                    sx={{ width: "100%", display: "flex", alignItems: "center" }}
                  >
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      {sessionExpiringSeconds < 60
                        ? t("sessionExpiringSeconds", {
                            seconds: Math.round(sessionExpiringSeconds),
                          })
                        : t("sessionExpiringMinutes", {
                            minutes: Math.round(sessionExpiringSeconds / 60),
                          })}
                      <Button
                        variant="contained"
                        size="small"
                        loading={isRefreshingSession}
                        onClick={async () => {
                          if (isRefreshingSession) {
                            return;
                          }
                          try {
                            setIsRefreshingSession(true);
                            await statsControllerApi.refreshStatsSession(userId, sessionId);
                            await refetchSession();
                          } catch (e) {
                            const message =
                              (e as unknown as AxiosError<{ message: string }>)?.response?.data
                                ?.message || e;

                            enqueueSnackbar(message as string, {
                              variant: "error",
                              autoHideDuration: 2000,
                            });
                          } finally {
                            setIsRefreshingSession(false);
                          }
                        }}
                      >
                        {t("extendSession")}
                      </Button>
                    </Box>
                  </Alert>
                )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography variant="subtitle2">
                  {t("sessionId")} {sessionId || "-"}
                </Typography>
              </Box>
              <MaterialReactTable table={sessionsTable} />
            </Stack>

            {sessionId && (
              <>
                <DataOutdatedAlert isDataOutdated={isDataOutdated} />
                <Box sx={{ display: "flex", minHeight: "400px", width: "100%" }}>
                  {isDiversityDataLoading && !isInitialized.current ? (
                    <CircularProgress sx={{ margin: "96px auto 32px" }} />
                  ) : (
                    <Box
                      sx={{
                        height: "700px",
                        width: "100%",
                        mb: 2,
                      }}
                    >
                      <MaterialReactTable table={table} />
                      <Box sx={{ mt: 4 }} />
                      {userId && sessionId && (
                        <SciChart
                          userId={userId}
                          sessionId={sessionId}
                          stats={statsListResponse?.data.items || []}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Grid>
      {!!buttonsContainer &&
        createPortal(
          <Box sx={{ position: "relative", bottom: "6px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDurable}
                  onChange={() => setIsDurable((prevState) => !prevState)}
                />
              }
              label={t("durable")}
            />
          </Box>,
          buttonsContainer
        )}
    </>
  );
};
